import { Dispatch, SetStateAction } from "react";
import { SelectedFilterInterface } from "storyblok/components";

import { getEducationCode } from "helpers/hooks/getEducationCode";

interface ParamOptions {
    [key: string]: string;
}

interface NewFilterOptions {
    [key: string]: string | number | Array<string> | Array<number>;
}

export function setParam({
    filterName,
    filter,
    currentParams
}: {
    filterName: string;
    filter: SelectedFilterInterface[
        | "certificationsFilter"
        | "departmentsFilter"
        | "zzpHoursFilter"
        | "minimumHoursFilter"
        | "maximumHoursFilter"
        | "regionsFilter"
        | "distanceFilter"
        | "employmentTypeFilter"];
    currentParams: URLSearchParams;
}) {
    const transformedFilterName = filterName.replace("Filter", "");
    const isArray = Array.isArray(filter);
    const isCertificationsFilter = filterName === "certificationsFilter";
    let certificationsFilter;

    // if filter is an array set to string and set param
    if (isArray) {
        // if filter is an array and empty remove param
        if (filter.length === 0) {
            currentParams.delete(transformedFilterName);
            return;
        }

        // certificationsFilter is a special case
        if (isCertificationsFilter) {
            certificationsFilter = (filter as Array<string>)
                .map((f: string) => getEducationCode(f))
                .join(",");

            currentParams.set("certifications", certificationsFilter);
            return;
        }

        if (filterName === "distanceFilter") {
            currentParams.set("place", filter[0] as string);
            currentParams.set("distance", filter[1] as string);
            return;
        }

        // if filter is an array and not empty set param
        currentParams.set(transformedFilterName, filter.join(","));
    }

    if (!isArray) {
        // if filter is not an array and empty remove param
        if (filter === undefined) {
            currentParams.delete(transformedFilterName);
            return;
        }
        // if filter is not an array and not empty set param
        currentParams.set(transformedFilterName, filter?.toString() || "");
    }
}

// remove param if filter has default values
export function cleanParam({
    filterName,
    state,
    currentParams
}: {
    filterName: string;
    state: SelectedFilterInterface;
    currentParams: URLSearchParams;
}) {
    const transformedFilterName = filterName.replace("Filter", "");

    switch (transformedFilterName) {
        case "employmentType":
            state.employmentTypeFilter.length === 0 &&
                currentParams.delete("employmentType");
            break;
        case "departments":
            state.departmentsFilter.length === 0 &&
                currentParams.delete("departments");
            break;
        case "certifications":
            state.certificationsFilter.length === 0 &&
                currentParams.delete("certifications");
            break;
        case "minimumHours":
            state.minimumHoursFilter === 0 &&
                currentParams.delete("minimumHours");
            break;
        case "maximumHours":
            state.maximumHoursFilter === 40 &&
                currentParams.delete("maximumHours");
            break;
        case "zzpHours":
            state.zzpHoursFilter.length === 0 &&
                currentParams.delete("zzpHours");
            break;
        case "regions":
            state.regionsFilter.length === 0 && currentParams.delete("regions");
            break;
        case "distance":
            if (
                state.distanceFilter.length === 0 ||
                !state.distanceFilter[0] ||
                !state.distanceFilter[1]
            ) {
                currentParams.delete("place");
                currentParams.delete("distance");
            }
            break;
        default:
            break;
    }
}

// This function uses both functions above to set and clean params
export function stateToParams(
    state: SelectedFilterInterface,
    currentParams: URLSearchParams
) {
    Object.keys(state).forEach(key => {
        setParam({
            currentParams,
            filter: state[key as keyof SelectedFilterInterface],
            filterName: key
        });

        cleanParam({
            currentParams,
            filterName: key,
            state
        });
    });

    return `${window.location.pathname}${
        currentParams.toString() ? `?${currentParams.toString()}` : ""
    }`;
}

// Takes params from url and transforms them to state
export function paramsToState(
    params: URLSearchParams,
    setState: Dispatch<SetStateAction<SelectedFilterInterface>>
) {
    const paramOptions = {} as ParamOptions;
    const newFilterOptions = {
        certificationsFilter: [],
        departmentsFilter: [],
        distanceFilter: [],
        employmentTypeFilter: [],
        maximumHoursFilter: 40,
        minimumHoursFilter: 0,
        regionsFilter: [],
        zzpHoursFilter: []
    } as NewFilterOptions;

    // extract params from url
    for (const [key, value] of params.entries()) {
        paramOptions[key] = value;
    }

    const keys = Object.keys(paramOptions);

    // transform params to state
    keys.forEach(key => {
        switch (key) {
            // certifications need to be transformed to education codes
            case "certifications":
                newFilterOptions[key + "Filter"] = paramOptions[key].split(",");

                break;
            // zzpHours need to be transformed to an array of numbers
            case "zzpHours":
                newFilterOptions[key + "Filter"] = paramOptions[key]
                    .split(",")
                    .map((zzpHourOption: string) => parseInt(zzpHourOption));
                break;
            // departments need to be transformed to an array of strings
            case "departments":
                newFilterOptions[key + "Filter"] = paramOptions[key].split(",");
                break;
            // regions work the same as departments
            case "regions":
                newFilterOptions[key + "Filter"] = paramOptions[key].split(",");
                break;
            case "employmentType":
                newFilterOptions[key + "Filter"] = paramOptions[key].split(",");
                break;
            // minimumHours and maximumHours need to be transformed to numbers
            case "minimumHours":
            case "maximumHours":
                newFilterOptions[key + "Filter"] = parseInt(paramOptions[key]);
                break;
            // Place and distance are part of the same filter
            case "place":
                (
                    newFilterOptions[
                        "distanceFilter"
                    ] as SelectedFilterInterface["distanceFilter"]
                )[0] = paramOptions[key];
                break;
            case "distance":
                (
                    newFilterOptions[
                        "distanceFilter"
                    ] as SelectedFilterInterface["distanceFilter"]
                )[1] = paramOptions[key];
                break;
            default:
                break;
        }
    });

    setState(newFilterOptions as unknown as SelectedFilterInterface);
}
